import { z } from 'zod';
import { SystemInfo } from './index.ts';

export const nonNullDate = (fieldName: string) =>
  z
    .any()
    .refine((v) => v !== null, { message: `${fieldName} must not be null` })
    .pipe(z.coerce.date());

export const OrganizationId = z.string().brand('OrganizationId');
export type OrganizationId = z.infer<typeof OrganizationId>;

export const OrganizationTypeId = z.string().brand('OrganizationTypeId');
export type OrganizationTypeId = z.infer<typeof OrganizationTypeId>;

export const OrganizationType = z.object({
  id: OrganizationTypeId,
  name: z.string(),
});
export type OrganizationType = z.infer<typeof OrganizationType>;

export const SellerId = z.string().brand('SellerId');
export type SellerId = z.infer<typeof SellerId>;

export const Seller = z.object({
  id: SellerId,
  name: z.string(),
});
export type Seller = z.infer<typeof Seller>;

export const Organization = z
  .object({
    id: OrganizationId,
    name: z.string(),
    validFrom: nonNullDate('validFrom'),
    organizationType: OrganizationType.optional(),
    seller: Seller.optional(),
    status: z.enum(['active', 'inactive']),
    accountLimitNum: z.number().min(0),
  })
  .extend(SystemInfo.shape);
export type Organization = z.infer<typeof Organization>;

export const AccountId = z.string().brand('AccountId');
export type AccountId = z.infer<typeof AccountId>;

export const AuthAccount = z.object({
  id: AccountId,
  // 人物に紐づく場合はemail, そうでなければオペレーション名となる
  name: z.string().brand('AuthAccount.name'),
});
export type AuthAccount = z.infer<typeof AuthAccount>;

export const MagazineId = z.string().brand('MagazineId');
export type MagazineId = z.infer<typeof MagazineId>;

export const Magazine = z.object({
  id: MagazineId,
  slug: z.string(),
  name: z.string(),
  accountLimitNum: z.number().min(0).optional(),
  accountCount: z.number().min(0).optional(),
});
export type Magazine = z.infer<typeof Magazine>;

export const Account = z
  .object({
    id: AccountId,
    auth0Id: z.string(),
    email: z.email(),
    name: z.string(),
    status: z.enum(['active', 'inactive']),
    isInvited: z.boolean().optional(),
    isAdmin: z.boolean().optional(),
  })
  .extend(SystemInfo.shape);
export type Account = z.infer<typeof Account>;

export const OrganizationWithAccountsAndMagazines = Organization.extend({
  accounts: z.array(Account),
  magazines: z.object({
    nbl: Magazine.optional(),
    shiryoban: Magazine.optional(),
    junkan: Magazine.optional(),
  }),
});
export type OrganizationWithAccountsAndMagazines = z.infer<
  typeof OrganizationWithAccountsAndMagazines
>;

export const AccountWithMagazines = Account.extend({
  magazineOptions: z.object({
    nbl: z.boolean(),
    shiryoban: z.boolean(),
    junkan: z.boolean(),
  }),
});
export type AccountWithMagazines = z.infer<typeof AccountWithMagazines>;
